import type { InputProps } from '~/common/components/ui/input';
import { Input } from '~/common/components/ui/input';
import type { ChangeEvent } from 'react';
import { useState, useEffect, useCallback, memo } from 'react';
import { cn } from '~/common/utils/styleUtil';

type Props = InputProps & { limit: number };

const CharCounter = memo(({ current, limit, className }: { current: number; limit: number; className?: string }) => (
  <div className={className}>{`${limit - current} / ${limit}`}</div>
));

CharCounter.displayName = 'CharCounter';

export function LimitedInput({
  name,
  value = '',
  className,
  defaultValue,
  limit,
  disabled,
  onChange,
  testId,
  placeholder,
  ...props
}: Props & { testId: string }) {
  const isControlled = defaultValue === undefined;
  const [inputValue, setInputValue] = useState(isControlled ? (value as string) : (defaultValue as string));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isControlled && value !== inputValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInputValue(value as string);
    }
  }, [value, inputValue, isControlled]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (newValue.length <= limit) {
        setInputValue(newValue);

        if (onChange) {
          onChange(e);
        }
      }
    },
    [limit, onChange]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div className="flex flex-col">
      <Input
        className={className}
        name={name}
        defaultValue={defaultValue}
        value={isControlled ? inputValue : undefined}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={testId}
        maxLength={limit}
        placeholder={placeholder}
        {...props}
      />
      <CharCounter
        current={inputValue.length}
        limit={limit}
        className={cn(
          'text-xs mt-1 text-right invisible',
          inputValue.length === 0 && 'text-secondary',
          isFocused && 'visible'
        )}
      />
    </div>
  );
}
